import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import useRedirectToErrorPage from "../errors/hooks/redirectToErrorPage";
import { CityAutocomplete } from "../../components/CityAutocomplete";
import FirstStepWizardTextField from "./components/FirstStepWizardTextField";
import FirstStepWizardTextFieldLabel from "./components/FirstStepWizardTextFieldLabel";

const inputFields = [
  { label: "Nome", state: "name" },
  { label: "Cognome", state: "last_name" },
  { label: "Indirizzo", state: "address" },
  {
    label: "Città",
    state: "citta",
    idState: "residence_city",
    type: "autoComplete",
  },
  {
    label: "CAP",
    state: "zip_code",
  },
  { label: "Data di Nascita", state: "birth_date", type: "date" },
  { label: "Codice Fiscale", state: "tax_code" },
  {
    label: "Città di Nascita",
    state: "cittaNascita",
    idState: "birth_city",
    isOptional: true,
    type: "autoComplete",
  },
  { label: "Città di Nascita (Se estera)", state: "foreign_birth_city", isOptional: true },
  { label: "Provincia di Nascita (Se estera)", state: "foreign_birth_province", isOptional: true },
];

export default function FirstStepWizard() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState();
  const redirectToErrorPage = useRedirectToErrorPage(errorMessage);
  let location = useLocation();
  const clientCode = useOutletContext();

  const [alertState, showAlert] = useState(false);
  const [allCities, setAllCities] = useState([]);

  const [formState, setFormState] = useState({
    name: "",
    last_name: "",
    address: "",
    residence_city: "",
    birth_date: "",
    tax_code: "",
    birth_city: "",
    zip_code: "",
    foreign_birth_city: "",
    foreign_birth_province: ""
  });

  const email = useRef("");

  useEffect(() => {
    axios
      .get(`https://app.deb00.it/api/fetch-places`)
      .then((response) => {
        if (response.data && response.data.data) {
          const mappedCities = response.data.data.map((item) => ({
            id: item.id,
            nome: item.description,
            provincia: item.province,
          }));
          setAllCities(mappedCities);
        }
      })
      .catch((err) => { });
  }, []);

  const handleInput = (state, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [state]: value,
    }));
  };
  const handleSearchCity = async (query) => {
    const cleanQuery = query.trim().toLowerCase();

    if (!cleanQuery || cleanQuery.length < 2) return [];

    const filtered = allCities.filter((city) =>
      city.nome.toLowerCase().includes(cleanQuery),
    );
    filtered.sort((a, b) => {
      const nameA = a.nome.toLowerCase();
      const nameB = b.nome.toLowerCase();

      if (nameA === cleanQuery && nameB !== cleanQuery) return -1;
      if (nameB === cleanQuery && nameA !== cleanQuery) return 1;

      const startsA = nameA.startsWith(cleanQuery);
      const startsB = nameB.startsWith(cleanQuery);

      if (startsA && !startsB) return -1;
      if (!startsA && startsB) return 1;

      return nameA.localeCompare(nameB);
    });

    return filtered.slice(0, 10);
  };

  const handleAutocompleteChange = (textState, idState, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [textState]: value,
      [idState]: "",
    }));
  };

  const handleAutocompleteSelect = (textState, idState, cityObject) => {
    setFormState((prevState) => ({
      ...prevState,
      [textState]: cityObject.nome,
      [idState]: cityObject.id,
    }));
  };

  const isFormValid = () => {
    if (formState["tax_code"].length < 16) return false;
    if (formState["zip_code"].length !== 5) return false;
    if (formState["birth_city"] === "" && (formState["foreign_birth_city"] === "" || formState["foreign_birth_province"] === "")) {
      return false;
    }

    const textFieldsValid = Object.keys(formState).every((key) => {
      if (key === "birth_city" || key === "foreign_birth_city" || key === "foreign_birth_province") return true;
      return !isEmpty(formState[key]);
    });

    return textFieldsValid;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    axios
      .get(`https://app.deb00.it/api/get-client-by-code/${clientCode}`)
      .then((response) => {
        if (response.data["status"] !== "success") {
          setErrorMessage(
            `Si è verificato un errore. Rieffettuare la procedura con un nuovo link. Se l'errore persiste contattare l'assistenza.${response.data["descrizione"]}`,
          );
        } else {
          setFormState((prevState) => ({
            ...prevState,
            name: response.data["data"]["name"] ?? "",
            last_name: response.data["data"]["last_name"] ?? "",
            address: response.data["data"]["address"] ?? "",
            residence_city: response.data["data"]["residence_city"] ?? "",
            birth_date: response.data["data"]["birth_date"] ?? "",
            birth_city: "",
            tax_code: response.data["data"]["tax_code"] ?? "",
            zip_code: response.data["data"]["zip_code"] ?? "",
            foreign_birth_city: response.data["data"]["foreign_birth_city"] ?? "",
            foreign_birth_province: response.data["data"]["foreign_birth_province"] ?? ""
          }));
          email.current = response.data["data"]["email"];
        }
      });
  }, [navigate, redirectToErrorPage, location, clientCode]);

  return (
    <>
      <div className="flex flex-col items-center">
        <h1 className="text-xl font-semibold text-orange-500">
          Inserisca i seguenti dati:
        </h1>
        <div className="h-12" />
        <div className={alertState ? "block" : "hidden"}>
          <AlertMissingFields />
        </div>
        <form
          className="flex flex-col items-center w-10/12 lg:w-4/12 "
          autoComplete="off"
          onSubmit={async (e) => {
            e.preventDefault();
            const valid = isFormValid();
            if (!valid) {
              showAlert(true);
              return;
            }
            let body = JSON.stringify({
              email: email.current,
              name: formState["name"],
              last_name: formState["last_name"],
              address: formState["address"],
              zip_code: formState["zip_code"],
              residence_city: formState["residence_city"],
              tax_code: formState["tax_code"],
              birth_date: formState["birth_date"],
              birth_city: formState["birth_city"],
              foreign_birth_city: formState["foreign_birth_city"],
              foreign_birth_province: formState["foreign_birth_province"],
            });

            await axios
              .post(`https://app.deb00.it/api/clients/update-wizard`, body, {
                headers: { 'Content-Type': 'application/json' }
              }
              )
              .then((response) => {
                if (response.data["status"] === "success") {
                  navigate("/istruzioni-secondo-step", {
                    state: {
                      key: { authorized: true },
                      email: email.current,
                    },
                  });
                } else {
                  setErrorMessage(
                    `Si è verificato un errore. Rieffettuare la procedura con un nuovo link. Se l'errore persiste contattare l'assistenza. ${response.data["descrizione"]}`,
                  );
                }
              });
          }}
        >
          <div className="grid w-full gap-x-4 gap-y-4 lg:gap-y-6 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-6">
            {inputFields.map(
              ({ label, state, isOptional, idState, type, span }) => (
                <div
                  key={state}
                  className={`${typeof span !== "undefined" ? span : `col-span-3`
                    } relative`}
                >

                  <FirstStepWizardTextFieldLabel
                    label={label}
                    isOptional={isOptional}
                    isEmpty={type === "autocomplete" ? isEmpty(formState[idState]) : isEmpty(formState[state])} />

                  {type === "autoComplete" ? (
                    <CityAutocomplete
                      label={label}
                      value={formState[state]}
                      isOptional={isOptional === null ? false : isOptional}
                      onSearch={handleSearchCity}
                      onChange={(val) =>
                        handleAutocompleteChange(state, idState, val)
                      }
                      onSelect={(item) =>
                        handleAutocompleteSelect(state, idState, item)
                      }
                      isError={
                        isEmpty(formState[state]) || isEmpty(formState[idState])
                      }
                    />
                  ) : (
                    <FirstStepWizardTextField
                      value={formState[state]}
                      state={state}
                      label={label}
                      type={type !== null ? type : ""}
                      isOptional={isOptional === null ? false : isOptional}
                      handleInput={handleInput}
                      isEmpty={isEmpty(formState[state])}
                    />
                  )}
                </div>
              ),
            )}
          </div>
          <SubmitButton />
        </form>
      </div>
    </>
  );
}

const SubmitButton = () => {
  const [hover, showHover] = useState(false);
  return (
    <>
      <div className="h-12"></div>
      <button
        className={`${hover ? "bg-orange-500" : "bg-transparent"} 
                ${hover ? "text-white" : "text-orange-500"} 
                border-orange-500 border-2 font-bold w-64 h-16 shadow-lg`}
        onMouseEnter={() => showHover(true)}
        onMouseLeave={() => showHover(false)}
        type="submit"
      >
        PROSEGUI
      </button>
      <div className="h-12"></div>
    </>
  );
};

const AlertMissingFields = () => {
  return (
    <>
      <div
        className="relative flex flex-col px-12 py-3 text-red-700 bg-red-100 border border-red-400 rounded"
        role="alert"
      >
        <strong className="font-bold">Convalida dei dati non riuscita</strong>
        <span className="block sm:inline">
          Completi tutti i campi e seleziona le città dalla lista.<br />
          Verificare che codice fiscale e CAP siano scritti correttamente.
        </span>
      </div>
      <div className="h-12"></div>
    </>
  );
};

const isEmpty = (value) => {
  return value === undefined || value === null || String(value).trim() === "";
};
